/**
 * Initialises every slider in the cart recommendations modal.
 *
 * cart-recommendations-modal.js resolves a single slider with querySelector, so
 * only the first group's carousel ever worked. The theme's own controller
 * already exposes init(root), which walks every [data-blaze-slider] beneath a
 * root - that is what every multi-slider section uses.
 *
 * Deliberately NOT a MutationObserver on the body: the controller mutates
 * attributes as it initialises, so observing attributes re-triggered this in a
 * loop and locked the page up. This waits for the modal to appear instead, on a
 * bounded poll, and initialises once per opening.
 */
(() => {
  const MODAL = '[data-modal="cart-recommendations"]';
  let lastRun = 0;

  const initAll = () => {
    const controller = window.themeBlazeSliderController;
    if (!controller?.init) return false;

    const modal = document.querySelector(MODAL);
    if (!modal || modal.getAttribute("aria-hidden") === "true") return false;

    const sliders = [...modal.querySelectorAll("[data-blaze-slider]")];
    if (!sliders.length) return false;

    // The controller keeps its own record of what it has initialised, so init()
    // silently skips a slider it has seen before even when the cart has replaced
    // the markup underneath it. Anything not currently marked as initialised is
    // destroyed first, which clears that record and lets init bind it again.
    sliders.forEach((slider) => {
      if (slider.getAttribute("data-slider-initialized") !== "true") {
        try {
          controller.destroy?.(slider);
        } catch (e) {
          /* nothing to destroy */
        }
      }
    });

    controller.init(modal);

    // Only done once every slider is bound. Returning true on the first call
    // stopped the poll before the cart's re-render had replaced the markup, so
    // the later groups were left with fresh, uninitialised sliders.
    return sliders.every((slider) => slider.getAttribute("data-slider-initialized") === "true");
  };

  // Bounded poll after any click that could open the modal - no persistent
  // observer, so there is nothing to feed back on itself.
  // Only the API mode re-renders on cart events; curated markup is static and
  // keeps its sliders, so this listener no longer has to fight a re-render.
  ["cart:updated", "cart:sections-rendered"].forEach((name) => {
    window.addEventListener(name, () => {
      if (document.querySelector('[data-modal="cart-recommendations"] [data-cart-recs-static]')) return;
      let attempts = 0;
      const timer = window.setInterval(() => {
        if (initAll() || (attempts += 1) > 40) window.clearInterval(timer);
      }, 150);
    });
  });

  document.addEventListener("click", (event) => {
    // Clicks inside the modal must not re-run init: re-initialising a slider
    // resets it to the first slide, so an arrow click advanced and then
    // snapped straight back.
    if (event.target.closest?.(MODAL)) return;

    const now = Date.now();
    if (now - lastRun < 500) return;
    lastRun = now;

    let attempts = 0;
    const timer = window.setInterval(() => {
      if (initAll() || (attempts += 1) > 20) window.clearInterval(timer);
    }, 150);
  });
})();
